import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import i18n from '../i18n';
import api from '../services/api';

export interface User {
  _id: string;
  name: string;
  email: string;
  age?: number;
  weight?: number;
  height?: number;
  gender?: 'male' | 'female';
  goal?: string;
  activityLevel?: string;
  daysPerWeek?: number;
  timePerSession?: number;
  workoutLocation?: 'home' | 'gym';
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  language?: string;
  subscriptionStatus?: string;
  subscriptionPlan?: string;
  subscriptionEndDate?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateProfile: (userData: any) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('authUser');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));

        try {
          const response = await api.auth.getUserProfile() as { success?: boolean; user?: User; message?: string };
          if (response?.success && response?.user) {
            setUser(response.user!);
            localStorage.setItem('authUser', JSON.stringify(response.user));
            syncLanguage(response.user?.language);
          } else {
            clearAuth();
          }
        } catch {
          // keep cached auth on network failure
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authUser');
    setToken(null);
    setUser(null);
  };

  const syncLanguage = (lang?: string) => {
    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang);
      localStorage.setItem('i18nextLng', lang);
      document.documentElement.lang = lang;
    }
  };

  const saveAuth = (user: User, token: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('authUser', JSON.stringify(user));
    setToken(token);
    setUser(user);
    syncLanguage(user.language);
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.auth.login(email, password) as { success?: boolean; token?: string; user?: User; message?: string };
      if (response?.success && response?.token && response?.user) {
        saveAuth(response.user, response.token);
      } else {
        setError(response?.message || 'Login failed');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const formatted = {
        name: userData.name?.trim(),
        email: userData.email?.trim().toLowerCase(),
        password: userData.password,
        age: userData.age ? Number(userData.age) : undefined,
        gender: userData.gender,
        weight: userData.weight ? Number(userData.weight) : undefined,
        height: userData.height ? Number(userData.height) : undefined,
        goal: userData.goals || userData.goal,
        activityLevel: userData.activityLevel,
        daysPerWeek: userData.daysPerWeek ? Number(userData.daysPerWeek) : undefined,
        timePerSession: userData.timePerSession ? Number(userData.timePerSession) : undefined,
        workoutLocation: userData.workoutLocation,
        experienceLevel: userData.experienceLevel,
        language: userData.language || 'en',
      };

      const response = await api.auth.register(formatted) as { success?: boolean; token?: string; user?: User; message?: string };
      if (response?.success && response?.token && response?.user) {
        saveAuth(response.user, response.token);
        return { success: true };
      }
      setError(response?.message || 'Registration failed');
      return { success: false, error: response.message };
    } catch (err: any) {
      const msg = err.message || 'Registration failed';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
    setError(null);
  };

  const updateProfile = async (data: any) => {
    setError(null);
    try {
      const response = await api.auth.updateUserProfile(data) as { success?: boolean; user?: User; message?: string };
      if (response?.success && response?.user) {
        setUser(response.user);
        localStorage.setItem('authUser', JSON.stringify(response.user));
        syncLanguage(response.user.language);
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.auth.getUserProfile() as { success?: boolean; user?: User };
      if (response?.success && response?.user) {
        setUser(response.user);
        localStorage.setItem('authUser', JSON.stringify(response.user));
      }
    } catch {
      // silent fail
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated: !!user, isLoading, error,
      login, register, logout, updateProfile, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
